import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { useHouseholdId } from './useHouseholdId'
import {
  isPushSupported,
  getPermissionState,
  getCurrentSubscription,
  requestPermission,
  subscribeToPush,
  unsubscribeFromPush,
} from '../lib/pushSubscription'
import { track } from '../lib/posthog'

// Single source of truth for the Settings toggle. Combines THREE signals:
//   1. Browser capability (isPushSupported)
//   2. OS permission state (Notification.permission)
//   3. Whether THIS browser's PushSubscription endpoint exists in the DB
//
// (3) is what `isSubscribed` reflects — a user signed in on multiple devices
// can have a subscription on device A and not on device B; the toggle on
// device B should read off, not on. The DB is keyed (user_id, endpoint).

export function usePushSubscription() {
  const { user }      = useAuth()
  const householdId   = useHouseholdId()
  const queryClient   = useQueryClient()
  const userId        = user?.id ?? null
  const supported     = isPushSupported()

  // Permission state lives in component state (vs derived) because
  // requestPermission() resolves with the new state and we want the UI to
  // re-render off it without forcing a full requery.
  const [permission, setPermission] = useState<NotificationPermission>(
    supported ? getPermissionState() : 'default',
  )

  // Window/tab focus can change permission (user toggled in browser settings).
  // Listen and refresh on visibility change so the UI reflects reality.
  useEffect(() => {
    if (!supported) return
    function refresh() { setPermission(getPermissionState()) }
    document.addEventListener('visibilitychange', refresh)
    return () => document.removeEventListener('visibilitychange', refresh)
  }, [supported])

  const { data: isSubscribed = false, isLoading: queryLoading } = useQuery({
    queryKey: ['push_subscription', userId],
    enabled:  !!userId && supported,
    queryFn:  async () => {
      const sub = await getCurrentSubscription()
      if (!sub) return false
      const { data } = await supabase
        .from('push_subscriptions')
        .select('id')
        .eq('user_id',  userId!)
        .eq('endpoint', sub.endpoint)
        .maybeSingle()
      return !!data
    },
  })

  const subscribeMutation = useMutation({
    mutationFn: async () => {
      if (!userId)      throw new Error('Not signed in')
      if (!householdId) throw new Error('No household — finish setup first')

      const newPerm = await requestPermission()
      setPermission(newPerm)
      if (newPerm !== 'granted') {
        throw new Error('Notifications blocked. Enable them in your browser settings.')
      }
      track('push_permission_granted')

      const sub = await subscribeToPush()

      // sub.toJSON().keys is the only public path to the raw p256dh / auth
      // bytes — the PushSubscription instance hides them behind getKey() in
      // ArrayBuffer form, but toJSON() emits already-base64-encoded strings
      // which is exactly what the DB column wants.
      const json = sub.toJSON()
      const keys = json.keys || {}
      const p256dh = keys.p256dh
      const auth   = keys.auth
      if (!p256dh || !auth) throw new Error('Subscription is missing required keys')

      const { error } = await supabase
        .from('push_subscriptions')
        .upsert(
          {
            user_id:      userId,
            household_id: householdId,
            endpoint:     sub.endpoint,
            p256dh,
            auth,
            user_agent:   navigator.userAgent,
          },
          { onConflict: 'user_id,endpoint' },
        )
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push_subscription', userId] })
    },
  })

  const unsubscribeMutation = useMutation({
    mutationFn: async () => {
      const sub      = await getCurrentSubscription()
      const endpoint = sub?.endpoint
      await unsubscribeFromPush()
      if (endpoint && userId) {
        await supabase
          .from('push_subscriptions')
          .delete()
          .eq('user_id',  userId)
          .eq('endpoint', endpoint)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push_subscription', userId] })
    },
  })

  return {
    isSubscribed,
    isSupported: supported,
    permission,
    subscribe:   () => subscribeMutation.mutateAsync(),
    unsubscribe: () => unsubscribeMutation.mutateAsync(),
    isLoading:   queryLoading || subscribeMutation.isPending || unsubscribeMutation.isPending,
    error:       (subscribeMutation.error as Error | null)?.message
              ?? (unsubscribeMutation.error as Error | null)?.message
              ?? null,
  }
}
